// src/components/LoginForm.tsx
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import DefaultLayout from "../layouts/default/Default.layout";
import Table from "../components/Table/Table";
import {useEffect, useMemo, useState} from "react";
import { Column, useTable } from "react-table";
import { Simulado } from "../../sdk/@types";
import { Button, Modal, Input } from "antd";
import {useNavigate, useParams} from "react-router-dom";
import usePageTitle from "../../core/hooks/UsePageTitle";
import {useSimulado} from "../../core/hooks/useSimulado";
import useInscrito from "../../core/hooks/useInscrito";
import { invalidateSimuladosCache } from "../../core/hooks/useNextSimulado";
import { InscricaoFeedback, InscricaoFeedbackOutcome } from "../components/SimuladoInscricaoAluno/InscricaoFeedback";
import {
  SimuladoInscricaoCards,
  formatarDataHora,
  ofereceInscricao,
  ofereceDesistencia,
} from "../components/SimuladoInscricaoAluno/SimuladoInscricaoCards";

const schema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(6, "Min 6 chars"),
});

type FormData = z.infer<typeof schema>;

export default function ListarSimulado() {
  const { simulados, filtereds, setFiltereds, fetchSimuladosDoUsuario, setSimulados } = useSimulado();
  const {registrarInscricao, removerInscricao} = useInscrito();
  const [filtered, setFiltered] = useState<Simulado.Simulado[]>()
  const {tipo} = useParams();
  const [showNickname, setShowNickname] = useState(false);
  const [nickname, setNickname] = useState('');
  const [mocktestSelected, setMocktestSelected] = useState<Simulado.Simulado | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [dialogFeedback, setDialogFeedback] = useState<Exclude<InscricaoFeedbackOutcome, 'sucesso'> | null>(null);
  const [successFeedback, setSuccessFeedback] = useState<string | null>(null);

  usePageTitle('Inscrição de Simulado');

  const navigate = useNavigate();

  const columns : Column<Simulado.Simulado>[] = useMemo(() => [
    {
      Header: 'Descrição',
      accessor: 'description',
    },
    {
      Header: 'Tipo',
      accessor: 'type',
    },
    {
      Header: 'Situação',
      accessor: 'situation',
    },
    {
      Header: 'Início',
      accessor: 'start',
      Cell: ({ value }) => (value ? <strong>{formatarDataHora(value)}</strong> : 'Indefinido'),
    },
    {
      Header: 'Fim',
      accessor: 'end',
      Cell: ({ value }) => (value ? <strong>{formatarDataHora(value)}</strong> : 'Indefinido'),
    },
    {
      Header: 'Ação',
      Cell:({row})=>(
        <div className="justify-center flex gap-2">
          {ofereceInscricao(row.original) && <Button type="primary" onClick={() => handleModalInscrever(row.original!)}>
            Inscrever
          </Button>}
          {ofereceDesistencia(row.original) && (
            <Button
              type="default"
              danger
              onClick={() => handleRemoverInscricao(row.original)}
            >
              Desistir
            </Button>
          )}

        </div>
      ),
    }
  ], []);

  const instance = useTable<Simulado.Simulado>({
    columns,
    data: filtered??[],
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  useEffect(() => {
    (async () => {
      handleUpdate();
    })();
  }, [tipo]);
  
  const handleUpdate = async () => {
    const list = await fetchSimuladosDoUsuario(); // return array directly
    const filtered =
      tipo === 'completa'
        ? list.filter(s => s.situation === "disponivel" && s.registered)
        : list.filter(s => s.type === tipo && s.situation === "disponivel");
    setFiltered(filtered);
  }


  const handleInscrever = async () => {
    if (enviando) return;

    const inscricao : Simulado.Inscrito = {
      situation: "inscrito",
      mocktest: mocktestSelected!,
      nick_name: nickname.trim() === '' ? undefined : nickname,
    }

    setEnviando(true);
    try {
      const outcome = await registrarInscricao(inscricao);
      if (outcome.kind === 'sucesso') {
        setDialogFeedback(null);
        setShowNickname(false);
        setNickname('');
        setSuccessFeedback(mocktestSelected?.description ?? '');
        invalidateSimuladosCache();
        await handleUpdate();
      } else if (outcome.kind === 'conflito') {
        setDialogFeedback('conflito');
        await handleUpdate();
      } else {
        setDialogFeedback('falha');
      }
    } finally {
      setEnviando(false);
    }
  }

  const handleModalInscrever = async (row : Simulado.Simulado) => {
    setDialogFeedback(null);
    setShowNickname(true);
    setMocktestSelected(row);
  }

  const handleRemoverInscricao = async (row : Simulado.Simulado) => {
    await removerInscricao(row.id!);
    await handleUpdate();
  }

  return (
    <DefaultLayout>
      <main>
        <div className="justify-start mb-2 border-b dark:border-slate-700 p-2">
          {tipo === 'completa'&&<p className="text-2xl font-bold text-gray-500 dark:text-gray-300">Simulados Agendados</p>}
          {tipo === 'completa'&&<p className="text-1x1 font-normal text-gray-500 dark:text-gray-300">
            Simulados agendados e aguardando início
          </p>}
          {tipo !== 'completa'&&<p className="text-2xl font-bold text-gray-500 dark:text-gray-300">Simulados {tipo === "competitivo"?"Competitivos":"Periódicos"} Disponíveis</p>}
          {tipo !== 'completa'&&<p className="text-1x1 font-normal text-gray-500 dark:text-gray-300">
            Simulados disponíveis para inscrição. Certifique-se de não escolher a mesma data de outro agendamento
          </p>}
        </div>

        {successFeedback && (
          <InscricaoFeedback
            outcome="sucesso"
            descricaoSimulado={successFeedback}
            className="mb-3"
          />
        )}

        <div className="hidden sm:block">
          <Table instance={instance} />
        </div>
        <div className="sm:hidden">
          <SimuladoInscricaoCards
            simulados={filtered ?? []}
            onInscrever={handleModalInscrever}
            onDesistir={handleRemoverInscricao}
          />
        </div>
      </main>
      <Modal
        open={showNickname}
        onCancel={() => setShowNickname(false)}
        title="Nome no Rank"
        onOk={handleInscrever}
        okText="Confirmar Inscrição"
        confirmLoading={enviando}
        okButtonProps={{ disabled: enviando }}
      >
        <p>Digite um apelido para ser exibido no ranking do simulado:</p>
        <p>Caso não seja informado, será usado o nome do usuário.</p>
        <Input
          placeholder="Digite seu apelido:"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          autoFocus
        />
        {dialogFeedback && (
          <InscricaoFeedback
            outcome={dialogFeedback}
            descricaoSimulado={mocktestSelected?.description ?? ''}
            className="mt-3"
          />
        )}
      </Modal>
    </DefaultLayout>
  );
}
